import React, { useEffect, useRef, useState } from 'react';
import { AppHeader } from '../components/app-header';
import { AppFooter } from '../components/app-footer';
import { EventNotificationService, useEventNotification } from '../framework/services/event-notification';
import { AppPages } from './app-pages';
import { BrowserRouter as Router } from 'react-router-dom';
import { getSinglePageAppStyles } from '../app-core/app-styles';
import { mergeStyles } from '@fluentui/react';
import { BwjSemiFlexContainer } from '../framework/components/bwj-semi-flex-container';
import { BwjScrollContainer } from '../framework/components/bwj-scroll-container';
import { BwjViewManager, BwjViewState } from '../framework/components/bwj-view-manager';
import { BwjButtonType } from '../framework/components/bwj-button';
import { ShopAppEvents, useToggleLoadingOverlay, useToggleShoppingPrefsPanel } from '../app-shop/events';
import { IAppHeaderConfiguration } from './entities/app-header-config';
import { useProjectConfiguration } from '../services/customer-account';
import { CustomerAccountPanel } from './components/account-panel';
import { StormDoorShoppingPrefsPanel } from './shop/components/st-door-pref-panel';
import { BwjEventArgs } from '../framework/entities/bwj-event';
import { BwjToaster } from '../framework/components/bwj-toaster';
import { ProjectCartManager } from './cart/components/cart-manager';
import { LoadingOverlay } from './components/loading-overlay';

const styles = getSinglePageAppStyles();

const subscriberId = 'Component.AppContent';
const resetMainContentScrollEvent = `${subscriberId}.ResetMainContentScroll`

export const AppContent: React.FunctionComponent = () => {
  const [showViews, setShowViews] = useState(false);
  const [shopContextAvailable, setShopContextAvailable] = useState(false);
  const [shoppingCartAvailable, setShoppingCartAvailable] = useState(false);
  const [projectAccountAvailable, setProjectAccountAvailable] = useState(false);
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const loadingMessage = useRef('Loading application...');
  const [showDesktopMenu, setShowDesktopMenu] = useState(true);
  const [invokeShowMobileMenu, setInvokeShowMobileMenu] = useState<BwjEventArgs<null> | undefined>(undefined);
  const [scrollResetInvocation, setScrollResetInvocation] = useState<BwjEventArgs<null> | undefined>(undefined);
  const projectConfiguration = useProjectConfiguration(subscriberId);
  const [pageAllowDesktopPanel, setPageAllowDesktopPanel] = useState(false);
  const pageAllowChangeEvent = useToggleShoppingPrefsPanel(subscriberId);
  const toggleLoadingOverlay = useToggleLoadingOverlay(subscriberId);
  useEffect(() => {
    if(!!pageAllowChangeEvent && pageAllowChangeEvent.accessible()) {
      setPageAllowDesktopPanel(pageAllowChangeEvent.getArgs());
    }
  }, [pageAllowChangeEvent]);

  const showLoadingView = (message: string) => {
    loadingMessage.current = message;
    setViewState(BwjViewState.Loading);
  }
  
  useEventNotification((update: IAppHeaderConfiguration) => {
    setShopContextAvailable(update.doorSelectionTools);
    setShoppingCartAvailable(update.cartPanel);
    setProjectAccountAvailable(update.accountPanel);
  },[], ShopAppEvents.UpdateHeaderConfiguration, subscriberId);
  useEventNotification(() => setScrollResetInvocation(new BwjEventArgs(null)), [], resetMainContentScrollEvent, subscriberId);
  useEventNotification((msg) => showLoadingView(msg), [], ShopAppEvents.ShowAppLoadingWithMessage, subscriberId);
  useEventNotification(() => setViewState(BwjViewState.Ready), [], ShopAppEvents.ShowAppReady, subscriberId);
  
  useEffect(() => {
    // only ever do this once.  We do this to ensure that no app view can fire a Menu Available / Unavailable event
    // before this parent component has subscribed to it
    if(!showViews) {
      window.setTimeout(() => {
        setShowViews(true);
        setViewState(BwjViewState.Ready);
      }, 100);
    }
  });

  const appBodyClass = mergeStyles([styles.root]);
  // const mainSectionClass = mergeStyles([styles.main]);
  const contentAreaClass = mergeStyles([styles.contentArea]);

  const onRestartApp = () => {
    window.location.reload();
  }

  const onCartRequestedViewStateChange = (vs: BwjViewState) => {
    loadingMessage.current = 'One moment please...';
    setViewState(vs);
  }

  return (
    <Router>
      <ProjectCartManager
        onChangeViewStateRequest={onCartRequestedViewStateChange}
      />
      <CustomerAccountPanel />
      <div className={appBodyClass}>
        <AppHeader
          showMenu={shopContextAvailable}
          showCart={shoppingCartAvailable}
          showCustomerAccount={projectAccountAvailable}
          onDesktopMenuButtonClick={() => setShowDesktopMenu(!showDesktopMenu)}
          onMobileMenuButtonClick={() => setInvokeShowMobileMenu(new BwjEventArgs(null))}
          homeowner={projectConfiguration.myName}
        />
        <BwjSemiFlexContainer
          styles={{
            root: { flexGrow: 1 },
            content: {
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'stretch',
              position: 'relative'
            }
          }}
          pinWidth={false}
          pinHeight={true}
        >
          <LoadingOverlay
            overlayDisplayArgs={toggleLoadingOverlay}
          />
          {shopContextAvailable &&
          <StormDoorShoppingPrefsPanel
            invokeShowMobilePanel={invokeShowMobileMenu}
            showDesktopPanel={showDesktopMenu}
            pageAllowDesktopPanel={pageAllowDesktopPanel}
          />}
          <section className={contentAreaClass}>
            <BwjViewManager
              state={viewState}
              loadingState={{
                message: loadingMessage.current,
              }}
              errorState={{
                icon: 'StatusErrorFull',
                message: 'We could not start the app due to an error - try restarting the app.  Please be sure to check your Internet connection',
                buttons: [{
                  buttonStyle: BwjButtonType.PrimaryStandard,
                  text: 'Restart app',
                  onClick: onRestartApp,
                }],
              }}
            >
              <BwjScrollContainer
                containerStyle={{ display: 'flex', flexDirection: 'column' }}
                invokeScrollReset={scrollResetInvocation}
              >
                <AppPages />
              </BwjScrollContainer>
            </BwjViewManager>
          </section>
        </BwjSemiFlexContainer>
        <AppFooter />
      </div>
      <BwjToaster
        ariaShowFullMessageLabel={'Display full notification'}
        ariaDismissLabel={'Dismiss notification'}
        closeDialogButtonText={'Close'}
      />
    </Router>
  );
};

export const useMainContentScrollReset = () => {
  const fn = () => EventNotificationService.dispatch(resetMainContentScrollEvent);
  return fn;
}